import { Button, TextField, useSnackbar } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { Box, Checkbox } from "@mui/material";
import { getWeek, getYear } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSaveTemplate } from "../../../api/useCalendarTemplateQueries";
import { useSelectedTemplates } from "hooks/useTemplates";
import { useWeekDate } from "../hooks/useWeekDate";

export const SaveWeekTemplate = () => {
  const { t } = useTranslation();
  const { date } = useWeekDate();

  const { enqueueSnackbar } = useSnackbar();

  const selectedTemplates = useSelectedTemplates();

  const [templateName, setTemplateName] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>(
    selectedTemplates.map((template) => template.templateId),
  );
  const { mutate: saveTemplates } = useSaveTemplate();

  const handleToggleTemplateId = (templateId: number) => () => {
    if (selectedTemplateIds.includes(templateId)) {
      setSelectedTemplateIds((prev) =>
        prev.filter((selectedId) => selectedId !== templateId),
      );
    } else {
      setSelectedTemplateIds((prev) => [...prev, templateId]);
    }
  };

  const handleSaveTemplate = () => {
    if (!templateName) {
      setTemplateNameError("Template name is required.");
      return;
    }

    const sourceWeek = getWeek(date, {
      weekStartsOn: 1,
      firstWeekContainsDate: 4,
    });
    const sourceYear = getYear(date);

    enqueueSnackbar({
      text: "Template was saved successfully",
    });

    saveTemplates({
      name: templateName,
      sourceWeek: `${sourceWeek}/${sourceYear}`,
      templateTypes: selectedTemplateIds,
    });
  };

  return (
    <Box sx={{ marginTop: "24px" }}>
      <Typography insensitive variant="h3">
        {t("CALENDAR_WEEK_TEMPLATES_MODAL_SAVE_SECTION_TITLE")}
      </Typography>
      <Typography
        insensitive
        variant="body"
        sx={{ color: "DARK_GREY", margin: "16px 0px" }}
      >
        {t("CALENDAR_WEEK_TEMPLATES_MODAL_SAVE_SECTION_DESCRIPTION")}
      </Typography>
      <TextField
        sx={{ width: "100%", marginBottom: "16px" }}
        label="Name"
        required={true}
        hideSensitiveData={false}
        placeholder={t(
          "CALENDAR_WEEK_TEMPLATES_MODAL_SAVE_SECTION_PLACEHOLDER",
        )}
        value={templateName}
        onChange={(e) => {
          if (e.target.value) {
            setTemplateNameError("");
          }
          setTemplateName(e.target.value);
        }}
        error={!!templateNameError}
        errorMessage={templateNameError}
      />
      {selectedTemplates.map(({ templateId, label }) => {
        return (
          <Box
            key={templateId}
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: "-12px",
            }}
          >
            <Checkbox
              value={templateId}
              checked={selectedTemplateIds.includes(templateId)}
              onChange={handleToggleTemplateId(templateId)}
            />
            <Typography insensitive variant="body">
              {label}
            </Typography>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          gap: "24px",
          marginTop: "24px",
        }}
      >
        <Button
          style={{ marginLeft: "10px" }}
          variant="primary"
          onClick={handleSaveTemplate}
        >
          {t("CALENDAR_WEEK_TEMPLATES_MODAL_SAVE_SECTION_SAVE_BUTTON")}
        </Button>
      </Box>
    </Box>
  );
};
